import { memo, useCallback, useMemo, useRef } from 'react';
import { useOnboarding } from '@wg/wows-react-uikit';
import Tabs, { TABS_THEMES } from '@wg/wows-react-uikit/components/Tabs/Tabs';

import { ONBOARDING_DIALOGS } from '~/const/onboarding';
import { CLANSTARS_REWARD_STATUSES, CLANSTARS_TABS } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { showCStarsRewardsDialog, showCStarsRefinedInfoDialog, closeAllDialogs } from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';
import { usePoshtaMarkersDeactivate } from '~/hooks/usePoshta';
import { useAppDispatch, useAppSelector } from '~/store';
import { clanStarsAccountRewardsSelector, clanStarsSelectedTabSelector } from '~/store/selectors/clanStarsSelectors';
import { currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { getClanstarsStateThunk } from '~/store/thunks/clanStarsThunks';
import { POSHTA_MARKER_NAME, POSHTA_MARKER_SOURCE } from '~/types/poshta';
import { isLoggedIn } from '~/utils/auth';
import { fetchMembers } from '~/Actions/ActionClanProfile';
import { actionsClanStars } from '~/Actions/ActionClanStars';

import styles from './ClanStars.scss';
import ClanStarsClanResults from './ClanStarsClanResults/ClanStarsClanResults';
import ClanStarsDivisions from './ClanStarsDivisions/ClanStarsDivisions';
import ClanStarsRules from './ClanStarsRules/ClanStarsRules';

import type { Tab } from '@wg/wows-react-uikit/components/Tabs/Tabs';

const pagePoshtaMarkers = [{ source: POSHTA_MARKER_SOURCE.DIVISION_STAR, name: POSHTA_MARKER_NAME.REWARD_AVAILABLE }];

const ClanStars = () => {
  const dispatch = useAppDispatch();

  const accountRewards = useAppSelector(clanStarsAccountRewardsSelector);
  const selectedTab = useAppSelector(clanStarsSelectedTabSelector);
  const currentAccount = useAppSelector(currentAccountSelector);

  const abortController = useRef<AbortController>(new AbortController());

  useOnboarding({
    stepName: ONBOARDING_DIALOGS.CLANSTARS_ROUTE_ENTRY,
    stepDelay: 550,
    onStepActivate: () => {
      dispatch(showCStarsRefinedInfoDialog());
    },
  });

  usePoshtaMarkersDeactivate(pagePoshtaMarkers);

  useMount(() => {
    dwhExport.push(DWH_EVENTS.CLANSTARS.OPEN_PAGE);

    if (isLoggedIn() && currentAccount.clanId) {
      void dispatch(fetchMembers(currentAccount.clanId));
    }

    void updateClanstarsState();

    return () => {
      abortController.current?.abort();
      dispatch(actionsClanStars.changeTab(CLANSTARS_TABS.DIVISION_STAR));
      dispatch(closeAllDialogs());
    };
  });

  const updateClanstarsState = async () => {
    await dispatch(getClanstarsStateThunk({ abortSignal: abortController.current.signal }));

    if (accountNewRewards.length > 0) {
      dispatch(showCStarsRewardsDialog(accountNewRewards));
    }
  };

  const accountNewRewards = useMemo(
    () =>
      accountRewards.filter(
        (reward) =>
          reward.status && [CLANSTARS_REWARD_STATUSES.NEW, CLANSTARS_REWARD_STATUSES.FAILED].includes(reward.status),
      ),
    [accountRewards],
  );

  const changeTab = useCallback(
    (tab: CLANSTARS_TABS) => {
      dispatch(actionsClanStars.changeTab(tab));
    },
    [dispatch],
  );

  const renderTabContent = useMemo(() => {
    switch (selectedTab) {
      case CLANSTARS_TABS.DIVISION_STAR:
        return <ClanStarsDivisions />;

      case CLANSTARS_TABS.RESULTS:
        return <ClanStarsClanResults />;

      case CLANSTARS_TABS.RULES:
        return <ClanStarsRules />;

      default:
        return null;
    }
  }, [selectedTab]);

  const tabsItems: Tab<CLANSTARS_TABS>[] = [
    {
      value: CLANSTARS_TABS.DIVISION_STAR,
      content: t('Звезда Отряда'),
    },
    {
      value: CLANSTARS_TABS.RESULTS,
      content: t('Результаты Клана'),
    },
    {
      value: CLANSTARS_TABS.RULES,
      content: t('Правила'),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabs}>
        <Tabs<CLANSTARS_TABS>
          classNameWrapper={styles.blurred}
          tabs={tabsItems}
          tabSelected={selectedTab}
          theme={TABS_THEMES.CLIENT}
          onToggleTab={changeTab}
        />
      </div>
      {renderTabContent}
    </div>
  );
};

export default memo(ClanStars);
